import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { Task } from "../model/task";

@Injectable({
  providedIn: "root",
})
export class TaskService {
  private apiUrl: string;
  private byIdUrl: string;
  public _currentTasks: BehaviorSubject<Task[]>;
  public selectedTask: BehaviorSubject<Task | null>;
  private _formState = new BehaviorSubject<Partial<Task>>({});
  constructor(
    private http: HttpClient,
    private env: EnvironmentHelper,
    private router: Router,
    private auth: AuthService
  ) {
    this._currentTasks = new BehaviorSubject<Task[]>([]);
    this.selectedTask = new BehaviorSubject<Task | null>(null);

    this.apiUrl = `${env.apiBaseUrl}task/`;
    this.byIdUrl = `${env.apiBaseUrl}task/`;
  }

  public getTasks(token: string, ticketId: number): Observable<Messages> {
    const url = `${this.apiUrl}by-ticket/${ticketId}`;

    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(url, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveTasks(response, observer),
          (error) => this.receiveTasks(error, observer)
        );
    });
  }

  public createTask(
    token: string,
    ticketId: number,
    task: Task
  ): Observable<Messages> {
    const url = `${this.apiUrl}`;
    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(url, task, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveTask(response, observer),
          (error) => this.receiveTask(error, observer)
        );
    });
  }

  public updateTask(
    token: string,
    ticketId: number,
    task: Task
  ): Observable<Messages> {
    if (!task.id) {
      return new Observable<Messages>((observer) => {
        observer.next(Messages.from({ task: ["task.id.required"] }));
      });
    }

    const url = `${this.apiUrl}by-id/${task.id}`;
    return new Observable<Messages>((observer) => {
      this.http
        .put<any>(url, task, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveTask(response, observer),
          (error) => this.receiveTask(error, observer)
        );
    });
  }

  public deleteTask(
    token: string,
    ticketId: number,
    taskId: number
  ): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${taskId}`;

    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(url, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveTask(response, observer),
          (error) => this.receiveTask(error, observer)
        );
    });
  }

  private receiveTasks(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Task[]>)?.body;
      if (!data) {
        observer.next(Messages.from({ task: ["task.unknown"] }));
        return;
      }
      this._currentTasks.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(Messages.from({ account: ["login.not.authorized"] }));
    } else {
      observer.next(Messages.from({ task: ["task.incomplete"] }));
    }
  }

  private receiveTask(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Task>)?.body;
      if (!data) {
        observer.next(Messages.from({ task: ["task.unknown"] }));
        return;
      }
      this.selectedTask.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(Messages.from({ account: ["login.not.authorized"] }));
    } else if (response.status === 400) {
      observer.next(Messages.from({ task: ["task.wrong.data"] }));
    } else if (response.status === 409 || response.status === 500) {
      observer.next(Messages.from((response as HttpErrorResponse)?.error));
    } else {
      observer.next(
        Messages.from({
          task: [
            "task.technical.error " + response.status,
            (response as HttpErrorResponse)?.error as string,
          ],
        })
      );
    }
  }

  public getTaskById(token: string, taskId: number): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(`${this.byIdUrl}${taskId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveTasks(response, observer),
          (error) => this.receiveTasks(error, observer)
        );
    });
  }

  public selectFromRouter() {
    const segments = this.getLastSuccessfulNavigationSegments();

    if (segments && segments.length > 0) {
      const taskId = segments[segments.length - 1];
      const token = this.auth.token;

      this.getTaskById(token, Number(taskId)).subscribe();
    } else {
      const emptyTask = Task.EMPTY;
      this.select(emptyTask);
    }
  }

  private getLastSuccessfulNavigationSegments(): string[] | null {
    const segments =
      this.router.lastSuccessfulNavigation?.finalUrl?.root.children["primary"]
        .segments;
    return segments ? segments.map((segment) => segment.path) : null;
  }

  public select(task: Task) {
    this.selectedTask.next(task);
  }
  public accept(task: Task[]) {
    this._currentTasks.next(task);
  }

  public get selectedTaskObservable(): Observable<Task | null> {
    return this.selectedTask.asObservable();
  }

  public get tasksObservable(): Observable<Task[]> {
    return this._currentTasks.asObservable();
  }

  public get tasksData(): Task[] {
    return this._currentTasks.value;
  }

  public get currentTask(): Task {
    const value = this.selectedTask.value;
    return value || Task.EMPTY;
  }
  get formState(): Observable<Partial<Task>> {
    return this._formState.asObservable();
  }

  setFormState(state: Partial<Task>) {
    this._formState.next(state);

    localStorage.setItem("taskFormBackup", JSON.stringify(state));
  }

  restoreFormState(): Partial<Task> {
    const savedState = localStorage.getItem("taskFormBackup");
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        this._formState.next(parsedState);

        return parsedState;
      } catch (e) {}
    }

    return {};
  }

  clearFormStateBackup() {
    localStorage.removeItem("taskFormBackup");
  }
}
